<script setup>
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import Icon from "./Icon.vue"
import Button from './Button.vue'
import Checkbox from "./Checkbox.vue"
import { mediaUploader, selectedImage } from '@utils/mediaUploader'
// import { useRouter, useRoute } from 'vue-router'
// const router = useRouter()
// const route = useRoute()
import VuePictureCropper, { cropper } from 'vue-picture-cropper'


const slots = useSlots()

const emit = defineEmits(['update:modelValue', 'change'])
const thumb = defineModel('thumb')

const props = defineProps({
    modelValue: {
        type: [String, Array],
        required: true
    },
    heightText: {
        type: String,
        default: '200px'
    },
    widthText: {
        type: String,
        default: '700px'
    },
    defaultSlotHeight: {
        type: String,
        default: 'content-fit'
    },
    defaultSlotWidth: {
        type: String,
        default: 'content-fit'
    },
    fallBackSlotHeight: {
        type: String,
        default: '200px'
    },
    fallBackSlotWidth: {
        type: String,
        default: '700px'
    },
    flex: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Upload Image'
    },
    dummyData: {
        type: [Array, String],
        default: [] || ''
    },
    dummy: {
        type: Boolean,
        default: false
    },
    dummyClearable: {
        type: Boolean,
        default: true
    },
    dummyThumb: {
        type: Boolean,
        default: true
    },
    name: {
        type: 'String',
        required: true
    },
    multiple: {
        type: Boolean,
        default: false
    },
    crop: {
        type: Boolean,
        default: false
    },
    ratio: {
        type: String,
        default: 16 / 1
    },
})


const imageSelected = computed({
    // retrive modal value and replace seleced image of uploader
    get: () => {
        if (!props.modelValue) return

        selectedImage.value[props.name] = props.modelValue
        props.dummyData = props.modelValue
        return props.modelValue
    },
    // when value change emit some events
    set: (newValue) => {
        // console.log(newValue)
        emit('update:modelValue', newValue)

        // emit change only when newValue is different from oldValue
        if (newValue !== props.modelValue) {
            emit('change', newValue)
        }
    }
})

// watch selected image of uploader and update imageSelected value
watch(selectedImage.value, (newValue, oldValue) => {
    if (!newValue[props.name]) return
    // console.log('MediaUploaderButton watch', newValue[props.name])
    if (props.dummy && props.multiple && !newValue[props.name].includes(thumb.value)) thumb.value = ''
    return imageSelected.value = newValue[props.name]
})

const containerSize = computed(() => {
    return slots.default ? `height: ${props.defaultSlotHeight}; width: ${props.defaultSlotWidth};` : `height: ${props.fallBackSlotHeight}; width: ${props.fallBackSlotWidth};`
})

const selectDummyImage = (image) => {
    // console.log(thumb.value, image)
    if (thumb.value == image) {
        thumb.value = ''
    } else {
        thumb.value = image
    }
}

const removeDummyImage = (image) => {
    if (props.multiple) {
        selectedImage.value[props.name].splice(selectedImage.value[props.name].findIndex((item) => item == image), 1)
    } else if (!props.multiple) {
        selectedImage.value[props.name] = ''
        props.dummyData = ''
    }
}



const isDummySelected = (image) => {
    return thumb.value == image
}

const onDrop = (event) => {
    mediaUploader.state.multiple = props.multiple
    mediaUploader.state.activeUploader = props.name
    mediaUploader.onDrop(event)
}

onMounted(() => {
    mediaUploader.fetchImages()
})

// const removeImage = (url) => {
//     mediaUploader.state.activeUploader = props.name
//     mediaUploader.selectImage(url, true)
//     if (thumb.value == url) {
//         thumb.value = ''
//     }
// emit('update:modelValue', imageSelected)
// }
// onUnmounted(() => {
//     console.log(route)
//     console.log('MediaUploaderButton onUnmounted')
// })
import { useRouter } from 'vue-router'
const router = useRouter()

router.beforeEach((to, from, next) => {
    if (to.name !== from.name) {
        selectedImage.value = {}
    }
    next()
})

</script>
<template>
<div>
    <div :style="containerSize" class="max-w-full" @drop.prevent="onDrop" @dragenter.prevent="mediaUploader.dragover"
        @dragover.prevent="mediaUploader.dragover" @dragleave.prevent="mediaUploader.dragleave" :class="{
            'ring ring-[#7F56D9] ring-opacity-50 rounded-lg': mediaUploader.state.dragover,
        }
            ">
        <!-- warning: do not remove this invisible div -->
        <div class="hidden">{{ imageSelected }}</div>
        <!-- Fallback slot  -->
        <div v-if="!slots.default" class="w-full h-full">
            <div v-if="props.loading" class="flex items-center justify-center w-full h-full text-lg text-gray-400">
                {{ props.loading === true ? 'Loading...' : props.loading }} </div>
            <!-- When image is not selected -->
            <div @click="mediaUploader.open('library', props.name, props.multiple, props.crop, props.ratio)"
                class="cursor-pointer w-full h-full py-4 px-6 flex flex-col items-center justify-center border border-[#EAECF0] rounded-xl hover:bg-[#f9fafb]">
                <div class="mb-3 w-[52px] h-[52px] bg-[#F2F4F7] p-2.5 rounded-full border-[6px] border-[#F9FAFB]">
                    <Icon name="uploadCloud02" size="20px" class="text-[#475467]" />
                </div>
                <div
                    class="text-[#475467] text-sm font-normal flex-wrap justify-center md:justify-normal flex md:gap-1">
                    <div class="text-[#6941C6] font-semibold">Click to upload</div> or drag and drop
                </div>
                <div class="text-[#475467] text-xs font-normal text-center md:text-start">JPG, PNG, GIF 
                    <!-- <span class="text-[#6941C6] font-semibold"> ({{ props.widthText }} x {{ props.heightText }}) </span> -->
                </div>
            </div>
            <!-- When image is selected -->
            <!-- <div class="flex justify-center w-full h-full gap-2 overflow-hidden bg-gray-100 rounded-lg">
                    <div v-for="image in imageSelected" :key="image" class="relative">
                        <img :src="image" class="w-auto h-full" :draggable="true" />
                        <Icon @click.prevent="removeImage(image)" name="trash01" size="35px" class="opacity-40 group-hover:opacity-100 hover:scale-110 transition absolute top-2 right-3 bg-[#fff] p-2 rounded-full cursor-pointer" />
                    </div>
                </div> -->
        </div>
        <!-- Default slot  -->
        <div v-else @click="mediaUploader.open('library', props.name, props.multiple, props.crop, props.ratio)"
            v-bind="$attrs" class="cursor-pointer media-uploader-default-slot"
            :style="`height: ${props.defaultSlotHeight}; width: ${props.defaultSlotWidth};`">
            <slot></slot>
        </div>
    </div>

    <!-- dummy image  -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5" v-if="props.dummy">
        <template v-if="props.multiple && props.dummyData.length">
            <div v-for="(image, index) in props.dummyData" :key="index"
                class="relative rounded-lg overflow-hidden flex items-center justify-center border border-[#EAECF0]">
                <Icon v-if="dummyClearable" @click="removeDummyImage(image)" name="xClose" size="16px"
                    class="rounded-full text-[#475467] bg-white absolute top-2 right-2 p-0.5 cursor-pointer hover:bg-[#f8f8f8]">
                </Icon>
                <img :src="image" class="w-full max-h-full bg-[#EAECF0]">
                <Checkbox v-if="props.dummyThumb" @click="selectDummyImage(image)" :checked="isDummySelected(image)"
                    checkboxClass="bg-white" class="absolute bottom-2 left-2"><span
                        class="text-xs font-medium text-white">Set as Thumb</span></Checkbox>
            </div>
        </template>
        <template v-if="!props.multiple && props.dummyData">
            <div class="relative rounded-lg overflow-hidden flex items-center justify-center border border-[#EAECF0]">
                <Icon v-if="dummyClearable" @click="removeDummyImage(image)" name="xClose" size="16px"
                    class="rounded-full text-[#475467] bg-white absolute top-2 right-2 p-0.5 cursor-pointer hover:bg-[#f8f8f8]">
                </Icon>
                <img :src="props.dummyData" class="w-full max-h-full bg-[#EAECF0]">
                <!-- <Checkbox @click="selectDummyImage(image)" :checked="isDummySelected(image)" checkboxClass="bg-white" class="absolute bottom-2 left-2"><span class="text-xs font-medium text-white">Set as Thumb</span></Checkbox> -->
            </div>
        </template>
    </div>
</div>
</template>